import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { displayPlayerName } from 'src/app/core/util/player-name/player-name.util';

export interface CommunityActivity {
  id: number;
  userID: number;
  username: string;
  isGuest?: boolean;
  profilePictureURL?: string;
  smallProfilePictureURL?: string;
  activityType: ActivityType;
  activityData: ActivityData;
  createdAt: string;
}

export type ActivityType =
  | 'user_online'
  | 'game_won'
  | 'game_lost'
  | 'user_registered'
  | 'achievement_earned'
  | 'status_updated'
  | 'streak_achieved';

export interface ActivityData {
  gameId?: string;
  numPlayers?: number;
  points?: number;
  opponentUsername?: string;
  achievementName?: string;
  status?: string;
  emoji?: string;
  streakCount?: number;
  level?: number;
  [key: string]: any; // For additional data fields
}

export interface CommunityActivityResponse {
  activities: CommunityActivity[];
  total: number;
  limit: number;
}

export interface FriendActivityResponse {
  activities: CommunityActivity[];
  total: number;
  limit: number;
}

@Injectable({
  providedIn: 'root',
})
export class CommunityActivityBackendService {
  private readonly COMMUNITY_BASE_ADDRESS = '/community-activity';
  private readonly FRIENDS_BASE_ADDRESS = '/friends';

  /**
   * @description constructor of CommunityActivityBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description gets recent community activities
   * @param {number} limit - number of activities to fetch (1-100, default: 50)
   * @returns {Promise<CommunityActivityResponse>}
   */
  public getRecentCommunityActivities(
    limit: number = 50
  ): Promise<CommunityActivityResponse> {
    const validLimit = Math.min(100, Math.max(1, limit));
    const url = `${this.COMMUNITY_BASE_ADDRESS}/recent?limit=${validLimit}`;

    return firstValueFrom(this.http.get<CommunityActivityResponse>(url));
  }

  /**
   * @description gets friend activity feed
   * @param {number} limit - number of activities to fetch (1-100, default: 20)
   * @returns {Promise<FriendActivityResponse>}
   */
  public getFriendActivityFeed(
    limit: number = 20
  ): Promise<FriendActivityResponse> {
    const validLimit = Math.min(100, Math.max(1, limit));
    const url = `${this.FRIENDS_BASE_ADDRESS}/activity-feed?limit=${validLimit}`;

    return firstValueFrom(this.http.get<FriendActivityResponse>(url));
  }

  /**
   * @description formats activity type to display string
   * @param {ActivityType} activityType - the activity type
   * @returns {string} formatted display string
   */
  public formatActivityType(activityType: ActivityType): string {
    switch (activityType) {
      case 'user_online':
        return 'ist online';
      case 'game_won':
        return 'gewann ein Spiel';
      case 'game_lost':
        return 'verlor ein Spiel';
      case 'user_registered':
        return 'hat sich registriert';
      case 'achievement_earned':
        return 'erreichte eine Errungenschaft';
      case 'status_updated':
        return 'aktualisierte den Status';
      case 'streak_achieved':
        return 'erreichte eine Siegesserie';
      default:
        return 'Aktivität';
    }
  }

  /**
   * @description gets icon for activity type
   * @param {ActivityType} activityType - the activity type
   * @returns {string} emoji icon
   */
  public getActivityIcon(activityType: ActivityType): string {
    switch (activityType) {
      case 'user_online':
        return '👥';
      case 'game_won':
        return '🎮';
      case 'game_lost':
        return '😢';
      case 'user_registered':
        return '🎉';
      case 'achievement_earned':
        return '🎯';
      case 'status_updated':
        return '💬';
      case 'streak_achieved':
        return '🔥';
      default:
        return '📊';
    }
  }

  /**
   * @description formats activity description
   * @param {CommunityActivity} activity - the activity object
   * @returns {string} formatted description
   */
  public formatActivityDescription(activity: CommunityActivity): string {
    const baseText = this.formatActivityType(activity.activityType);
    const name = displayPlayerName(activity);
    
    switch (activity.activityType) {
      case 'achievement_earned':
        const achievementName = activity.activityData.achievementName;
        if (achievementName) {
          return `${name} erreichte "${achievementName}"`;
        }
        return `${name} ${baseText}`;
      
      case 'streak_achieved':
        const streakCount = activity.activityData.streakCount;
        if (streakCount) {
          return `${name} erreichte eine ${streakCount}er Siegesserie`;
        }
        return `${name} ${baseText}`;
      
      case 'game_won':
        const opponentUsername = activity.activityData.opponentUsername;
        const opponent = opponentUsername && displayPlayerName({ username: opponentUsername });
        const points = activity.activityData.points;
        if (opponent && points) {
          return `${name} gewann gegen ${opponent} (${points} Punkte)`;
        } else if (opponent) {
          return `${name} gewann gegen ${opponent}`;
        }
        return `${name} ${baseText}`;
      
      case 'status_updated':
        const status = activity.activityData.status;
        const emoji = activity.activityData.emoji;
        if (status) {
          const statusText = emoji ? `${emoji} ${status}` : status;
          return `${name} aktualisierte Status: "${statusText}"`;
        }
        return `${name} ${baseText}`;
      
      default:
        return `${name} ${baseText}`;
    }
  }

  /**
   * @description formats time ago from date string
   * @param {string} dateString - ISO date string
   * @returns {string} formatted time ago
   */
  public formatTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
      return 'gerade';
    } else if (diffMinutes < 60) {
      return `vor ${diffMinutes} Min`;
    } else if (diffHours < 24) {
      return `vor ${diffHours} Std`;
    } else if (diffDays === 1) {
      return 'gestern';
    } else {
      return `vor ${diffDays} Tagen`;
    }
  }
}
